import {Button} from 'antd';
import {useCallback, useState} from 'react';

import {appRoute} from '../../../app-route';
import {Meta} from '../../../component/meta/meta';
import {BreadCrumbs} from '../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../layout/page/page';
import {PageHeader} from '../../../layout/page-header/page-header';
import {PageSubHeader} from '../../../layout/page-header/page-sub-header';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {useModal} from '../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../provider/snackbar/snackbar-hook';
import {
    defaultGroupTemplateData,
    fetchTemplateGroupDelete,
    ResponseTemplateGroupType,
    useResponseTemplateGroupList,
} from '../../../service/reviews/reviews-response-templates';
import {findInArray} from '../../../util/array';
import {toTrimmedString} from '../../../util/string';
import {ResponseTemplatesActionSelect} from '../response-templates/response-templates-action-select/response-templates-action-select';

import {ResponseTemplatesEditGroupTable} from './response-templates-edit-group-table/response-templates-edit-group-table';

export function ResponseTemplatesEditGroup(): JSX.Element {
    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {modal} = useModal();
    const [responseTemplateGroupIdList, setResponseTemplateGroupIdList] = useState<Array<string>>([]);
    const [newGroup, setNewGroup] = useState<Array<ResponseTemplateGroupType>>([]);
    const {
        data: responseTemplateGroupListResult,
        isFetching: responseTemplateGroupListIsInProgress,
        refetch: refreshGroupList,
    } = useResponseTemplateGroupList();

    const handleDelete = useCallback(
        (responseTemplateGroupData?: ResponseTemplateGroupType) => {
            const groupTitleList: string = responseTemplateGroupData
                ? responseTemplateGroupData.title
                : responseTemplateGroupIdList
                      .map((responseTemplateGroupId: string): string => {
                          const responseTemplateGroup = findInArray<ResponseTemplateGroupType>(
                              responseTemplateGroupListResult || [],
                              {id: Number(responseTemplateGroupId)}
                          );

                          return toTrimmedString(responseTemplateGroup?.title);
                      })
                      .filter(Boolean)
                      .map((responseTemplateGroupTitle: string): string => `"${responseTemplateGroupTitle}"`)
                      .join(', ');

            modal.confirm({
                maskClosable: true,
                title: <Locale stringKey="RESPONSE_TEMPLATES__REMOVING_A_TEMPLATE_GROUP__HEADER" />,
                content: (
                    <Locale
                        stringKey="RESPONSE_TEMPLATES__REMOVING_A_TEMPLATE_GROUP__CONTENT"
                        valueMap={{
                            responseTemplateTitle: groupTitleList,
                        }}
                    />
                ),
                okText: <Locale stringKey="TEXT__APPLY" />,
                cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
                cancelButtonProps: {type: 'primary'},
                okButtonProps: {type: 'default', danger: true},
                onOk: async () => {
                    try {
                        await fetchTemplateGroupDelete({
                            ids: responseTemplateGroupData ? responseTemplateGroupData.id : responseTemplateGroupIdList,
                        });

                        snackbar.success(<Locale stringKey="TAGS__GROUP__DELETING_SUCCESS" />);
                        refreshGroupList();
                    } catch {
                        snackbar.error(<Locale stringKey="TAGS__GROUP__DELETING_ERROR" />);
                    }
                },
            });
        },
        [responseTemplateGroupIdList, modal, responseTemplateGroupListResult, snackbar, refreshGroupList]
    );

    const {
        reviewsManagementResponseTemplates: reviewsManagementResponseTemplatesRoute,
        reviewsManagementResponseTemplatesEditGroup: reviewsManagementResponseTemplatesEditGroupRoute,
    } = appRoute;

    return (
        <Page>
            <Meta title={getLocalizedString('RESPONSE_TEMPLATES__TEMPLATE_GROUPS')} />

            <BreadCrumbs
                list={[
                    {
                        path: reviewsManagementResponseTemplatesRoute.path,
                        titleLangKey: 'CATEGORY_NAME__RESPONSE_TEMPLATES',
                    },
                    {
                        path: reviewsManagementResponseTemplatesEditGroupRoute.path,
                        titleLangKey: 'RESPONSE_TEMPLATES__TEMPLATE_GROUPS',
                    },
                ]}
            />

            <PageHeader>
                <Locale stringKey="RESPONSE_TEMPLATES__TEMPLATE_GROUPS" />

                <Button
                    disabled={newGroup.length > 0}
                    onClick={() => {
                        setNewGroup([...newGroup, {...defaultGroupTemplateData}]);
                    }}
                    type="primary"
                >
                    <Locale stringKey="BUTTON__ADD_GROUP" />
                </Button>
            </PageHeader>

            <PageSubHeader>
                <Locale stringKey="TEXT__LIST_OF_GROUPS" />

                <ResponseTemplatesActionSelect
                    isActive={responseTemplateGroupIdList.length > 0}
                    isGroup
                    onDelete={handleDelete}
                />
            </PageSubHeader>

            <ResponseTemplatesEditGroupTable
                newGroup={newGroup}
                onDelete={handleDelete}
                refreshGroupList={refreshGroupList}
                responseTemplateGroupListIsInProgress={responseTemplateGroupListIsInProgress}
                responseTemplateGroupListResult={responseTemplateGroupListResult || []}
                setNewGroup={setNewGroup}
                setResponseTemplateGroupIdList={setResponseTemplateGroupIdList}
            />
        </Page>
    );
}
